import * as React from 'react';
import { useEffect } from 'react';
import CommonCard from './CommonCard';
import { GdsAggregationServiceGroup } from '../../../../Checkout/types';
import { getOrderStatus, getTravellers } from '../../../../Checkout/store/order/selectors';
import { useSelector } from 'react-redux';
import { NASProduct, NASProductCategory, OrderStatus } from '@websky/graphql';
import { isServicesSaving } from '../../../../Checkout/store/servicesIsSaving/selectors';
import { useToggleable } from '../../../../hooks';
import Dialog from '../../../../Service/components/Dialog/Dialog';
import Inner from '../Inner/Inner';
import Rules from '../Rules/Rules';
import { useEveryPort } from '../everyPort.hooks';
import { useMediaQuery } from 'react-responsive';
import { MOBILE_MIN_WIDTH } from '../../../../utils';
import WebskyAnalyticsApollo from '../../../../DataLayerAnalytics/WebskyAnalyticsApollo';
import { WxEvents } from '../../../../DataLayerAnalytics/types';
import { useNasServiceEventToggleable } from '../../../../Checkout/components/Checkout/hooks';

interface Props {
	segmentId: string;
	category: NASProductCategory;
	header: string;
	service: GdsAggregationServiceGroup;
	description: string;
	coverClassName: string;
	icons: React.ReactNode[];
}

const WrappedCommonCard: React.FC<Props> = props => {
	const [isRulesOpen, setRulesOpen] = React.useState(false);

	const orderStatus = useSelector(getOrderStatus);
	const isReadonly = orderStatus === OrderStatus.Cancelled;

	const serviceSaving = useSelector(isServicesSaving);
	const travellers = useSelector(getTravellers);

	const { isOpen, open, close } = useToggleable(false);

	const { services, selectedSummary, onServiceSave, availableSegments, selectedServices, priceFrom } = useEveryPort(
		props.category,
		props.segmentId,
		isReadonly
	);

	useNasServiceEventToggleable({
		services,
		serviceTypes: [props.category],
		onOpen: open
	});

	useEffect(() => {
		if (!isOpen || !services.length) {
			return;
		}

		if (services.length > 1) {
			WebskyAnalyticsApollo.dispatchWxViewEvent(WxEvents.NASVipList, {
				nasType: services[0]?.type,
				servicesIds: services.map(service => service.id)
			});
		} else {
			WebskyAnalyticsApollo.dispatchWxViewEvent(WxEvents.NASVipItem, {
				segmentId: props.segmentId,
				servicesIds: [services[0].id]
			});
		}
	}, [isOpen, services]);

	const isMobile = useMediaQuery({ maxWidth: MOBILE_MIN_WIDTH });

	const openDetails = () => {
		if (!!selectedSummary?.rules?.length) {
			setRulesOpen(true);
		} else if (selectedSummary?.detailsLink) {
			window.location.href = selectedSummary.detailsLink;
		}
	};

	const closeDetails = () => setRulesOpen(false);

	return (
		<>
			<CommonCard
				{...props}
				serviceSaving={serviceSaving}
				travellers={travellers}
				priceFrom={priceFrom}
				totalPrice={selectedSummary?.price}
				openDetails={openDetails}
				isSelected={selectedSummary.texts?.length > 0}
				selectedServicesText={selectedSummary.texts}
				selectedServicesPassengerNames={selectedSummary.passengerNames}
				selectedServicesDetailsLink={selectedSummary.detailsLink}
				isModifyAvailable={selectedSummary.modifyAvailable}
				onOpen={open}
				serviceType={props.service.type}
			/>

			{isOpen && (
				<Dialog
					isOpen={isOpen}
					onClose={close}
					dialogType="fullscreen"
					hideHeader={!isMobile}
					dialogHeader={props.header}
					content={
						<Inner
							everyPortServices={services as NASProduct[]}
							selectedServices={selectedServices}
							segments={availableSegments}
							onServiceSave={onServiceSave}
							onClose={close}
							segmentId={props.segmentId}
							loading={serviceSaving}
							travellers={travellers}
							type={props.category}
							header={props.header}
							readonly={isReadonly}
						/>
					}
				/>
			)}

			<Rules rules={selectedSummary?.rules ?? []} open={isRulesOpen} onClose={closeDetails} />
		</>
	);
};

export default WrappedCommonCard;
